import { useEffect, useState } from 'react';
import { cassa, scontrini } from '../../api/scontrini';
import type { DettaglioScontrino, SituazioneTecnico } from '../../api/scontrini';
import { formattaDataOra, formattaImporto } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';

const STATI: Record<string, string> = {
  DaEmettere: 'da emettere',
  Emesso: 'emesso',
  DaVerificare: 'da verificare',
  Annullato: 'annullato',
};

/*  GLI INCASSI DEL TECNICO E IL VERSAMENTO (22/10/2026). In alto quanto deve versare —
    i contanti emessi dall'ultimo versamento — e il bottone per confermarlo; sotto gli
    scontrini di oggi, contanti e POS, cosi' a fine giornata sa cosa ha incassato.  */
export function MieiIncassi() {
  const [situazione, setSituazione] = useState<SituazioneTecnico | null>(null);
  const [oggi, setOggi] = useState<DettaglioScontrino[] | null>(null);
  const [importo, setImporto] = useState('');
  const [note, setNote] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);

  const carica = () => {
    Promise.all([cassa.mia(), scontrini.miei()])
      .then(([s, o]) => {
        setSituazione(s);
        setOggi(o);
        setImporto(s.daVersare > 0 ? String(s.daVersare).replace('.', ',') : '');
      })
      .catch((e: Error) => setErrore(e.message));
  };
  useEffect(carica, []);

  const valore = numeroDecimale(importo);

  async function versa() {
    if (valore === null || valore <= 0) return;
    setInCorso(true);
    setErrore(null);
    setFatto(null);
    try {
      await cassa.versa({ importo: valore, note: note.trim() === '' ? null : note.trim() });
      setFatto(`Versati ${formattaImporto(valore)} nella cassa di Novara.`);
      setNote('');
      carica();
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  const contantiOggi = (oggi ?? []).filter((s) => s.stato === 'Emesso' && s.pagamento === 'Contanti');
  const posOggi = (oggi ?? []).filter((s) => s.stato === 'Emesso' && s.pagamento === 'Pos');
  const somma = (elenco: DettaglioScontrino[]) => elenco.reduce((t, s) => t + s.totale, 0);

  return (
    <div className="rapportino scontrino">
      <header className="testata-mobile">
        <h1>I miei incassi</h1>
      </header>

      {situazione && (
        <section className="da-versare">
          <p className="totale-ore">
            Da versare <strong>{formattaImporto(situazione.daVersare)}</strong>
          </p>
          <p className="nota">
            {situazione.ultimoVersamentoIl
              ? `Contanti degli scontrini emessi dopo l’ultimo versamento (${formattaDataOra(situazione.ultimoVersamentoIl)}).`
              : 'Contanti di tutti gli scontrini emessi finora.'}
          </p>
          {situazione.daVersare > 0 && (
            <>
              <label className="campo-blocco">
                <span>Verso</span>
                <input inputMode="decimal" value={importo} onChange={(e) => setImporto(e.target.value)} />
              </label>
              <label className="campo-blocco">
                <span>Note</span>
                <input value={note} placeholder="se versi meno, il perché" onChange={(e) => setNote(e.target.value)} />
              </label>
              <button
                type="button"
                className="primario"
                disabled={inCorso || valore === null || valore <= 0}
                onClick={() => void versa()}
              >
                Confermo il versamento
              </button>
            </>
          )}
        </section>
      )}

      {fatto && <p className="messaggio">{fatto}</p>}
      {errore && <p className="errore">{errore}</p>}

      {oggi && (
        <section>
          <p className="totale-ore">
            Oggi: contanti <strong>{formattaImporto(somma(contantiOggi))}</strong> · POS{' '}
            <strong>{formattaImporto(somma(posOggi))}</strong>
          </p>
          {oggi.length === 0 && <p className="nota">Nessuno scontrino oggi.</p>}
          {oggi.map((s) => (
            <div key={s.id} className="riga-materiale">
              <span className="desc">
                {s.numero} · {s.righe.map((r) => r.descrizione).join(', ')}
              </span>
              <span className="qta">{s.pagamento === 'Contanti' ? 'contanti' : 'POS'}</span>
              <span className={`qta stato-${s.stato}`}>{STATI[s.stato] ?? s.stato}</span>
              <span className="qta">{formattaImporto(s.totale)}</span>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
